import { initializeApp, type FirebaseApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import {
  connectFirestoreEmulator,
  disableNetwork,
  enableNetwork,
  initializeFirestore,
  persistentLocalCache,
  persistentSingleTabManager,
  type Firestore,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';
import {
  firestoreIdbNames,
  isRecoverableFirestoreError,
  shouldAdvanceCacheVersion,
} from './firestoreRecovery';
import { recordRecoverySignal } from './sentry';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// docs/28 offline / old2 `firebase.ts` 逐語移植 (2026-05-30 分析ツール parity)。
// Firestore オフラインキャッシュ (IndexedDB) の手動バスター。
// Firestore 設定スキーマや outputFormat / 分析データ解釈の破壊的変更を行ったときに、ここを bump すると
// 全クライアントが次回起動時に古い IndexedDB を削除して最新を取り直す。
// localStorage に最新バージョンを保存しているので、同じバージョンならスキップ (idempotent)。
// 2026-06-04: Firestore SDK 内部アサーション (INTERNAL ASSERTION FAILED: Unexpected state, #7341) で
// 一部ユーザーが投稿/登録不能に。壊れた watch/lease 状態が IndexedDB に焼き付き、リロードでも復帰しない
// ため、版数を bump して全クライアントの永続キャッシュを 1 回作り直す (= 焼き付き解消)。
// 併せて persistentMultipleTabManager → persistentSingleTabManager に戻している (下記参照)。
const FIRESTORE_CACHE_VERSION = '2026-06-04-firestore-assertion-recovery';
const CACHE_VERSION_KEY = 'app_firestore_cache_version';
// env-2: キャッシュバスター (TLA) のタイムアウト (ms)。これを過ぎたら削除完了を待たずにブートを進める。
const CACHE_BUST_TIMEOUT_MS = 3000;

if (typeof window !== 'undefined' && typeof indexedDB !== 'undefined') {
  try {
    const stored = window.localStorage?.getItem(CACHE_VERSION_KEY);
    if (stored !== FIRESTORE_CACHE_VERSION) {
      // initializeFirestore より先に Firestore の IndexedDB を破棄する。
      // top-level await: モダンブラウザ (Chrome 89+ / Safari 15.4+) で動作。
      // indexedDB.databases() は Safari < 16 で未実装のため、列挙できないときのみ実名フォールバックを使う。
      // deleteByName は「確実に消えた (success)」を true、それ以外 (blocked / error) を false で返す。
      // ARCB-1: onblocked は別タブが DB を掴んでいて**実削除されていない**状態。旧実装はこれも resolve()
      //         扱いにして版数を前進させ「治療済み」と誤認していた。blocked のときは版数を書かず、
      //         次回起動で再削除を試みられるようにする。存在しない DB の deleteDatabase は success
      //         (no-op) になるため、フォールバック名の空振りは false にはならない。
      const deletionPromises: Promise<boolean>[] = [];
      const deleteByName = (name: string) =>
        new Promise<boolean>((resolve) => {
          let settled = false;
          const done = (ok: boolean) => {
            if (!settled) {
              settled = true;
              resolve(ok);
            }
          };
          const req = indexedDB.deleteDatabase(name);
          req.onsuccess = () => done(true);
          req.onerror = () => done(false);
          req.onblocked = () => done(false);
        });

      // env-2: 削除処理が永久 pending (deleteDatabase がどのイベントも発火しない / databases() が
      //   返らない等) になると、この TLA が解決せず firebase.ts モジュール評価が完了しない。
      //   firebase.ts を静的 import する main.tsx の createRoot().render() に到達できず全画面ブランク固着。
      //   対処: 列挙+削除を runCacheBust() に切り出し、CACHE_BUST_TIMEOUT_MS のタイムアウトと Promise.race
      //   する。削除が終わらなくてもブートは進む。タイムアウト時は版数を前進させない (= 次回起動で再削除)。
      const runCacheBust = async (): Promise<boolean> => {
        let enumerated = false;
        if (typeof indexedDB.databases === 'function') {
          try {
            const dbs = await indexedDB.databases();
            for (const info of dbs) {
              if (info.name && info.name.startsWith('firestore/')) {
                deletionPromises.push(deleteByName(info.name));
              }
            }
            enumerated = true;
          } catch {
            /* fallback to known names below */
          }
        }
        // ARCB-6: indexedDB.databases() 非対応 (Safari < 16 / 旧 iOS) では列挙できないため、SDK の実名を
        //         直接削除する。実名 = `firestore/[DEFAULT]/<projectId>/main` (firestoreRecovery.ts で検証)。
        //         列挙できた場合は既に対象を網羅しているので二重に積まない。
        if (!enumerated && firebaseConfig.projectId) {
          for (const name of firestoreIdbNames(firebaseConfig.projectId)) {
            deletionPromises.push(deleteByName(name));
          }
        }
        const results = await Promise.all(deletionPromises);
        return results.every(Boolean);
      };

      const timeout = new Promise<'timeout'>((resolve) => {
        window.setTimeout(() => resolve('timeout'), CACHE_BUST_TIMEOUT_MS);
      });
      const outcome = await Promise.race([runCacheBust(), timeout]);

      if (shouldAdvanceCacheVersion(outcome)) {
        try {
          window.localStorage?.setItem(CACHE_VERSION_KEY, FIRESTORE_CACHE_VERSION);
        } catch {
          /* localStorage 不可 (プライベートモード等) でも致命的ではない */
        }
        console.info('[firestore-cache] bumped to', FIRESTORE_CACHE_VERSION);
      } else {
        // ARCB-1 / env-2: blocked/error/timeout で未削除。版数を前進させず、次回起動で再試行させる。
        console.warn(
          '[firestore-cache] not advanced (blocked/failed/timeout); will retry next load',
        );
      }
    }
  } catch (e) {
    console.warn('[firestore-cache] cache bust failed (continuing):', e);
  }
}

export const app: FirebaseApp = initializeApp(firebaseConfig);
export const auth: Auth = getAuth(app);
// user 第 15 弾 (U-P6): Firestore persistent local cache を有効化。
// IndexedDB に doc キャッシュを永続化することで、オフライン時も最後に読んだ doc を read 可能。
// 旧 v1 は enableIndexedDbPersistence (legacy API) を使用、v2 は initializeFirestore + persistentLocalCache を採用 (Firebase SDK v9.7+ 推奨)。
//
// 2026-06-04: tabManager を persistentMultipleTabManager → persistentSingleTabManager({ forceOwnership: true }) に変更。
// 理由: マルチタブ調整 (タブ間 IndexedDB lease + watch 状態共有) が Firestore SDK の内部アサーション
//       (INTERNAL ASSERTION FAILED: Unexpected state, firebase-js-sdk #7341) の代表的な誘発要因で、
//       本番で一部ユーザーが投稿/登録不能 (赤いエラーで固着) になったため。
//       docs/03 §7.5-3 の正式設計 (単一タブ + forceOwnership) に実装を復帰させる。第15弾で multipleTab に
//       逸脱していたのを戻す。forceOwnership=最新タブが IndexedDB lease を取得 (Safari 背景タブ suspend デッドロック回避)。
export const db: Firestore = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentSingleTabManager({ forceOwnership: true }),
  }),
});
export const storage: FirebaseStorage = getStorage(app);
// docs/03 §5 で region 統一 asia-northeast1
export const functions: Functions = getFunctions(app, 'asia-northeast1');

if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

// docs/28 offline / old2 `firebase.ts` L101-118 逐語移植 (2026-05-30 分析ツール parity)。
// オフライン時に Firestore SDK を cache-only モードへ強制切替する。
// 機内モードのまま新しい onSnapshot/getDoc を発火すると、SDK が「ネットワーク復帰待ち」に入り
// cache 即時応答が遅延する事象 (Firestore SDK の既知挙動) を回避するため。
// → 分析ツールの各タブ (matrix/map/insights/ideas/network/heatmap) を機内モードで開いても
//   永続キャッシュから即時表示でき、loading スピナーが回り続けない。
// NOTE: connectFirestoreEmulator より後に呼ぶ (enableNetwork は Firestore 操作扱いになり得るため、
//       emulator 接続設定を確定させてから network state を適用する)。
if (typeof window !== 'undefined') {
  const applyNetworkState = () => {
    if (typeof navigator === 'undefined') return;
    if (navigator.onLine) {
      enableNetwork(db).catch((e) => console.warn('[firestore] enableNetwork failed', e));
    } else {
      disableNetwork(db).catch((e) => console.warn('[firestore] disableNetwork failed', e));
    }
  };
  window.addEventListener('online', applyNetworkState);
  window.addEventListener('offline', applyNetworkState);
  // 起動直後の状態にも反映 (機内モードで起動した場合は即 cache-only)
  applyNetworkState();
}

// 2026-06-04: Firestore SDK 内部アサーション (INTERNAL ASSERTION FAILED: Unexpected state, #7341) からの
// 自動復帰。このアサーションが出ると Firestore クライアント全体が壊れ、以降の read/write が全滅する。
// しかも壊れた状態が IndexedDB 永続キャッシュに残ると **通常リロードでは復帰しない** (本番で観測:
// 特定ユーザーが赤いエラーのまま固着し、リロードしても同じ)。
// 対処: このアサーションを検知したら、キャッシュバスター版数を消して次回起動で壊れた IndexedDB を破棄させ、
//       reload で作り直す。無限リロード防止に「1 セッション 1 回まで」へ制限する。
// 発火経路: 主に onSnapshot リスナーの watch ストリーム (uncaught → 'error') と、稀に await されない
//          Promise (→ 'unhandledrejection') の両方を拾う。
// MT-4 (2026-06-07): 検知対象を `isRecoverableFirestoreError` (firestoreRecovery.ts) に集約し、
//   #7341 内部アサーションに加えて「The client has already been terminated.」(別タブの deleteDatabase が
//   当該タブを terminate した固着) と「not in the required state」も自動 reload 対象に含めた。
const RECOVERED_FLAG_KEY = 'cw_fs_assertion_recovered';

/**
 * #7341 等の回復可能な Firestore 内部アサーションからの自動復帰 (1 セッション 1 回、window 環境のみ作用)。
 *
 * 通常は window の 'error' / 'unhandledrejection' リスナー (下記) が拾うが、**投稿 setDoc の同期パスで
 * 発火したアサーションを呼び側 (PostForm submit) の try/catch が吸収する**ケースでは window に届かない
 * (ARCB-4、docs/111 Wave 2)。そのため module-level に export し、呼び側 catch から
 * `isRecoverableFirestoreError(err)` 判定後に直接呼べるようにする。
 */
export function recoverFromFirestoreAssertion(reason: unknown): void {
  if (typeof window === 'undefined') return;
  // 1 セッション 1 回まで (壊れたキャッシュを消した後もまだ自爆する=別原因のときに無限ループしない)。
  try {
    if (window.sessionStorage?.getItem(RECOVERED_FLAG_KEY)) return;
    window.sessionStorage?.setItem(RECOVERED_FLAG_KEY, '1');
  } catch {
    /* sessionStorage 不可 (プライベートモード等) でも続行 */
  }
  // R-69: #7341 / 自動復帰の発火を Sentry に報告するためのマーカーを残す (reload を跨いで
  // 次回起動の initSentry が報告する。Sentry の window handler はこの reload に間に合わないため)。
  recordRecoverySignal(reason);
  try {
    // 次回起動時にキャッシュバスターが古い (壊れた) Firestore IndexedDB を破棄するよう版数を消す。
    window.localStorage?.removeItem(CACHE_VERSION_KEY);
  } catch {
    /* noop */
  }
  console.warn('[firestore] internal assertion detected → clearing persistent cache and reloading');
  window.location.reload();
}

if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (e) => {
    if (isRecoverableFirestoreError(e.reason)) recoverFromFirestoreAssertion(e.reason);
  });
  window.addEventListener('error', (e) => {
    const reason = e.error ?? e.message;
    if (isRecoverableFirestoreError(reason)) recoverFromFirestoreAssertion(reason);
  });
}
